import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

// ---------------------------------------------------------------------------
// Wave-2 Lane C — chat transcript surface localization.
//
// chat-messages.tsx renders the message-by-message chat/task history for the
// IDE chat rail. Before this module every non-`strings.chat.*`,
// non-`chromeCopy`, non-`executionCopy` literal in that file was raw English,
// including the capability-confirmation card's `undo-failed` status line
// (wave-1 final review, deferred item #20). This module is the single
// locale-keyed home for that remaining literal surface: chip/label text,
// composed status sentences, and the creator/developer capability
// readback+undo prose. Selection logic (regex classification, status
// switches) stays in chat-messages.tsx; this module holds ONLY the resulting
// strings per locale, mirroring the split `studio-execution-copy.ts` uses
// between `resolveStudioExecutionErrorCopy` (logic) and its `COPY` table
// (data).
//
// OUT OF SCOPE (left as literal English, not moved into this table):
//  - The five capability-card action buttons ("Confirm", "Cancel", "Undo",
//    "Recover", "Retry undo") and the details-toggle ("Show all N changes" /
//    "Show fewer changes"). All six are resolved by ACCESSIBLE NAME in
//    `getByRole('button', { name: ... })` across chat-messages.test.tsx with
//    no `data-testid` to fall back to — the exact fragility the existing
//    in-file comment on the Confirm button documents ("it works today only
//    because this label is NOT localized, and it would break the day it
//    is"). Localizing them needs a testid migration first; that is a
//    separate, larger change than this lane's scope.
//  - The terminology-selection EXCEPTION-path outcome copy (declared /
//    phrasing-only / dismissed / no-project / persist-failed /
//    scope-conflict) and the validation-summary `unreadable` line: both are
//    pre-existing hardcoded-Korean literals that this file's own comments
//    document as a deliberate, already-reviewed convention on this surface
//    (matches the generation-readiness panel). Not a new gap this lane
//    introduced; left untouched to avoid re-opening that decision.
//  - `stripShaclJargon`'s literal replacement text: it operates on
//    `formatShaclMessage`'s already-dynamic (and itself unlocalized) raw
//    validator prose, so translating half of that composite string would
//    make the result MORE bilingual, not less.
//  - Raw identifiers/engine vocabulary: tool ids, IRIs, `changeKind`
//    (INSERT/UPDATE/DELETE) in the expanded realtime-update list, `SHACL`
//    itself. These are data, not copy.
// ---------------------------------------------------------------------------

/** Mirrors chat-messages.tsx's local `CapabilityCardStatus` union — the
 * canonical definition lives here so both files stay in sync without a
 * circular import (chat-messages.tsx imports this type back). */
export type ChatCapabilityCardStatus =
  | "pending"
  | "cancelling"
  | "executing"
  | "executed"
  | "cancelled"
  | "failed"
  | "recovery-needed"
  | "undo-failed"
  | "undone";

export interface ChatMessagesCopy {
  common: {
    none: string;
  };
  actionFlowTrace: {
    heading: string;
    stateTopologyHeading: string;
    pathsCount: (count: number) => string;
    event: string;
    flow: string;
    domains: string;
    stepOrder: string;
    stateDiffHeading: string;
    renderer: string;
  };
  compose: {
    ragPlanLabel: string;
    buildPlanLabel: string;
    readyLabel: string;
    summary: (sectionCount: number, blockedCount: number) => string;
    pages: string;
    bindings: string;
    blockedLabel: string;
    candidateBlocks: string;
    blockedBadge: string;
    intentRatio: (matched: number, total: number) => string;
    intentScore: (score: string) => string;
    notBound: string;
    evidenceState: (state: string) => string;
    moreCandidates: (count: number) => string;
    bindPreviewHeading: string;
    stateLabel: string;
    actionsLabel: string;
    dataLabel: string;
    supported: string;
    partial: string;
    unsupported: string;
  };
  mutation: {
    appliedVerb: Record<"add_block" | "modify_action" | "merge_branch", string>;
    appliedPrefix: string;
    triplesInserted: (count: number) => string;
    moreChips: (count: number) => string;
    viewProjectDetails: string;
    transportError: string;
    shaclViolations: (count: number) => string;
    toolWrapper: (tool: string) => string;
    tryFix: string;
    /** Main clause, WITHOUT the latency detail — rendered as plain text,
     * paired with a separate monospace `latencyDetail` span (matches the
     * two-tone convention every other AiBubble metadata line in this file
     * uses). */
    anotherUserChangeText: (kind: "INSERT" | "UPDATE" | "DELETE") => string;
    latencyDetail: (latencyMs: number) => string;
    anotherUserChangesText: (count: number) => string;
    lastLatencyDetail: (lastLatencyMs: number) => string;
    /** Label preceding the version sequence number in the grouped
     * realtime-update event list (`<label> <versionSeq>`), e.g. "seq 4". */
    eventSeqLabel: string;
  };
  /**
   * Wave 4 lane 1 — the central bus-execution-seam disclosure card
   * (`studio-mcp-bus.ts`'s `executeWithAuthority`, `agent-worklog` kind,
   * `card: 'agent-action'`). Baked into the message's `title`/`entries[].label`
   * fields AT PRODUCER TIME (via `getLocale()` + `getChatMessagesCopy()`
   * inside the bus, a non-React module) because `worklogCardTitle` renders
   * `card.title` verbatim whenever any entry lacks a `code` — true here by
   * design, since `toolLabel`/`targetLabel` wrap dynamic per-call data the
   * count-shaped `WorklogEntryCopy` functions cannot carry. Tool/target
   * VALUES stay raw identifiers (see the "Raw identifiers" note above) —
   * only these two label words and the title are copy.
   */
  agentDisclosure: {
    title: string;
    toolLabel: string;
    targetLabel: string;
  };
  validation: {
    passedAcross: (passCount: number, blockCount: number) => string;
    violations: (count: number) => string;
    warningsSuffix: (count: number) => string;
    moreViolations: (count: number) => string;
    blockGoneTitle: string;
  };
  ai: {
    foundInstantly: string;
    creatingApp: (pageCount: number, estimateSeconds: number) => string;
    appReady: string;
    applied: string;
  };
  capability: {
    statusZero: Record<ChatCapabilityCardStatus | "default", string>;
    statusWithCount: {
      changeCount: (count: number) => string;
      cancelling: (changes: string) => string;
      applying: (changes: string) => string;
      applied: (changes: string) => string;
      cancelled: (changes: string) => string;
      failed: (changes: string) => string;
      recoveryNeeded: (changes: string) => string;
      undoFailed: (changes: string) => string;
      undone: (changes: string) => string;
      default: (changes: string) => string;
    };
    confirmFailedGeneric: string;
    cancelUnavailable: string;
    verificationLabel: string;
    undoLabel: string;
    beforeLabel: string;
    afterLabel: string;
    missingChangesWarning: string;
    typeToConfirm: (phrase: string) => string;
    /**
     * P2 (finding 3): the requirement, stated WITHOUT the phrase inlined —
     * the card renders the phrase itself next to it as an isolated,
     * selectable `<code>`. `typeToConfirm` buried an ALL-CAPS English token
     * (`RENAME HOME` — `step5ConfirmationPhrase()`, an English verb + the
     * uppercased internal page id) mid-sentence, so a Korean operator could
     * not see where the token they must transcribe began or ended.
     */
    typeToConfirmHint: string;
    /**
     * P2 (finding 3): humanized names for the change TARGETS the Studio
     * operation impact registry declares
     * (`features/chat/services/studio-operation-impact-contract.ts` —
     * `pageMetaPaths()` fixes exactly these four field keys). Before this,
     * a page-title rename rendered as the raw field path
     * `App · manifest pages home title`. Any path shape the registry does
     * NOT declare still falls back to `humanizeTechnicalLabel(change.label)`
     * unchanged — this table names what the registry already names, it does
     * not invent labels for arbitrary paths.
     */
    changeTargets: {
      pageMeta: Record<
        "title" | "description" | "route" | "layout",
        string
      >;
    };
    statusSpans: {
      applying: string;
      cancelling: string;
      applied: string;
      cancelled: string;
      needsRecovery: string;
      undone: string;
    };
    /**
     * Fix round (post-review) — the five capability-card action buttons plus
     * the details-toggle. Resolution in chat-messages.test.tsx moved from
     * `getByRole('button', { name: ... })` to `getByTestId(...)` (see
     * `capability-confirm`/`-cancel`/`-undo`/`-recover`/`-retry-undo`/
     * `-details-toggle` in chat-messages.tsx) precisely so these labels
     * could be localized without breaking the ~25 call sites that used to
     * resolve them by their (previously English-only) accessible name. The
     * buttons still HAVE accessible names — now these localized ones.
     */
    actions: {
      confirm: string;
      cancel: string;
      requestChanges: string;
      undo: string;
      recover: string;
      retryUndo: string;
      showAllChanges: (count: number) => string;
      showFewerChanges: string;
    };
    pendingReadback: {
      secret: string;
      release: string;
      exportBundle: string;
      importIngest: string;
      openNavigate: string;
      validateReview: string;
      fallback: string;
    };
    readback: {
      cancelling: string;
      executing: string;
      executed: string;
      cancelled: string;
      failed: string;
      recoveryNeeded: string;
      undoFailed: string;
      undone: string;
    };
    undo: {
      nothingApplied: string;
      cancelling: string;
      failed: string;
      recoveryAvailable: string;
      recoveryUnavailable: string;
      undoFailed: string;
      undone: string;
      executing: string;
      executedAvailable: string;
      executedUnavailable: string;
      noInverse: string;
      cannotUndoLocally: string;
      default: string;
    };
    developerReadback: {
      cancelling: (readback: string) => string;
      executing: (readback: string) => string;
      executed: (readback: string) => string;
      cancelled: (readback: string) => string;
      failed: (readback: string) => string;
      recoveryNeeded: (readback: string, rollbackPhrase: string) => string;
      undoFailed: (readback: string) => string;
      undone: (readback: string) => string;
      rollbackDidNotComplete: string;
      rollbackWasUnavailable: string;
    };
    developerUndo: {
      cancelling: (undo: string) => string;
      executing: (undo: string) => string;
      executedAvailable: (undo: string) => string;
      executedUnavailable: (undo: string) => string;
      cancelled: (undo: string) => string;
      failed: (undo: string) => string;
      recoveryAvailable: (undo: string, rollbackPhrase: string) => string;
      recoveryUnavailable: (undo: string, rollbackPhrase: string) => string;
      undoFailed: (undo: string) => string;
      undone: (undo: string) => string;
      rollbackFailedPhrase: string;
      rollbackWasUnavailablePhrase: string;
    };
  };
}

const changeUnitEn = (count: number) => `${count} change${count === 1 ? "" : "s"}`;
const violationUnitEn = (count: number) => `${count} violation${count === 1 ? "" : "s"}`;
const warningUnitEn = (count: number) => `${count} warning${count === 1 ? "" : "s"}`;
const blockUnitEn = (count: number) => `${count === 1 ? "block" : "blocks"}`;
const tripleUnitEn = (count: number) => `${count === 1 ? "triple" : "triples"}`;
const changeKindWordsEn: Record<"INSERT" | "UPDATE" | "DELETE", string> = {
  INSERT: "an insert",
  UPDATE: "an update",
  DELETE: "a delete",
};

const en: ChatMessagesCopy = {
  common: { none: "none" },
  actionFlowTrace: {
    heading: "Logic runtime",
    stateTopologyHeading: "State topology",
    pathsCount: (count) => `${count} paths`,
    event: "Event",
    flow: "Flow",
    domains: "Domains",
    stepOrder: "Step order",
    stateDiffHeading: "State diff reflected in Preview",
    renderer: "Renderer",
  },
  compose: {
    ragPlanLabel: "RAG plan",
    buildPlanLabel: "Build plan",
    readyLabel: "ready",
    summary: (sectionCount, blockedCount) => {
      const sections =
        sectionCount === 1 ? "1 section" : `${sectionCount} sections`;
      const tail =
        blockedCount > 0
          ? ` — ${blockedCount} ${blockedCount === 1 ? "needs" : "need"} a closer look before building`
          : " — all ready to build";
      return `${sections} planned${tail}.`;
    },
    pages: "Pages",
    bindings: "Bindings",
    blockedLabel: "Blocked",
    candidateBlocks: "Candidate blocks",
    blockedBadge: "blocked",
    intentRatio: (matched, total) => `intent ${matched}/${total}`,
    intentScore: (score) => `intent ${score}`,
    notBound: "not bound",
    evidenceState: (state) => `evidence ${state}`,
    moreCandidates: (count) => `+${count} more candidates`,
    bindPreviewHeading: "Bind preview",
    stateLabel: "State",
    actionsLabel: "Actions",
    dataLabel: "Data",
    supported: "supported",
    partial: "partial",
    unsupported: "unsupported",
  },
  mutation: {
    appliedVerb: {
      add_block: "Added a block",
      modify_action: "Updated behavior",
      merge_branch: "Merged changes",
    },
    appliedPrefix: "Applied",
    triplesInserted: (count) => `${count} ${tripleUnitEn(count)} inserted`,
    moreChips: (count) => `+${count} more`,
    viewProjectDetails: "View project details",
    transportError: "Transport error",
    shaclViolations: (count) => `${count} SHACL violation${count === 1 ? "" : "s"}`,
    toolWrapper: (tool) => `(tool: ${tool})`,
    tryFix: "Try a fix",
    anotherUserChangeText: (kind) =>
      `Another user made ${changeKindWordsEn[kind]} change`,
    latencyDetail: (latencyMs) => `(latency ${latencyMs}ms)`,
    anotherUserChangesText: (count) => `Another user made ${count} changes`,
    lastLatencyDetail: (lastLatencyMs) => `(last ${lastLatencyMs}ms ago)`,
    eventSeqLabel: "seq",
  },
  agentDisclosure: {
    title: "Agent action completed",
    toolLabel: "Tool",
    targetLabel: "Target",
  },
  validation: {
    passedAcross: (passCount, blockCount) =>
      `${passCount} passed across ${blockCount} ${blockUnitEn(blockCount)}`,
    violations: (count) => violationUnitEn(count),
    warningsSuffix: (count) => `, ${warningUnitEn(count)}`,
    moreViolations: (count) =>
      `+ ${count} more violation${count === 1 ? "" : "s"} · open checks`,
    blockGoneTitle:
      "This block no longer exists — re-run validation for current results.",
  },
  ai: {
    foundInstantly: "Found instantly — pulled from a verified pattern.",
    creatingApp: (pageCount, estimateSeconds) =>
      `Creating your app — ${pageCount} pages (~${estimateSeconds}s)`,
    appReady: "App is ready",
    applied: "Applied",
  },
  capability: {
    statusZero: {
      pending: "Exact change paths are required before approval",
      cancelling: "Cancelling proposal",
      executing: "Applying proposal",
      executed: "Proposal applied",
      cancelled: "Proposal cancelled",
      failed: "Proposal could not be applied",
      "recovery-needed": "Proposal needs recovery",
      "undo-failed": "Proposal applied; undo failed",
      undone: "Proposal undone",
      default: "Exact change paths are required before approval",
    },
    statusWithCount: {
      changeCount: (count) => changeUnitEn(count),
      cancelling: (changes) => `Cancelling ${changes}`,
      applying: (changes) => `Applying ${changes}`,
      applied: (changes) => `${changes} applied`,
      cancelled: (changes) => `${changes} cancelled`,
      failed: (changes) => `${changes} could not be applied`,
      recoveryNeeded: (changes) => `${changes} needs recovery`,
      undoFailed: (changes) => `${changes} applied; undo failed`,
      undone: (changes) => `${changes} undone`,
      default: (changes) => `${changes} ready for review`,
    },
    confirmFailedGeneric: "Capability confirmation failed.",
    cancelUnavailable: "This confirmation is no longer available.",
    verificationLabel: "Verification",
    undoLabel: "Undo",
    beforeLabel: "Before",
    afterLabel: "After",
    missingChangesWarning:
      "Exact change paths are missing. Cancel this proposal and request a fresh one.",
    typeToConfirm: (phrase) => `Type ${phrase} to continue`,
    typeToConfirmHint: "Type this exact phrase in the box below to continue:",
    changeTargets: {
      pageMeta: {
        title: "Page title",
        description: "Page description",
        route: "Page address",
        layout: "Page layout",
      },
    },
    statusSpans: {
      applying: "Applying...",
      cancelling: "Cancelling...",
      applied: "Applied",
      cancelled: "Cancelled",
      needsRecovery: "Needs recovery",
      undone: "Undone",
    },
    actions: {
      confirm: "Confirm",
      cancel: "Cancel",
      requestChanges: "Request changes",
      undo: "Undo",
      recover: "Recover",
      retryUndo: "Retry undo",
      showAllChanges: (count) => `Show all ${count} changes`,
      showFewerChanges: "Show fewer changes",
    },
    pendingReadback: {
      secret: "Studio will validate the connection without revealing the secret.",
      release:
        "Studio will keep the release result for review. Check the destination before treating it as live.",
      exportBundle: "Studio will verify the exported file before marking it ready.",
      importIngest:
        "Studio will verify the imported result in this workspace. The external source stays unchanged unless you explicitly sync it.",
      openNavigate:
        "Studio will confirm that the requested workspace opens without a load error.",
      validateReview: "Studio will show the check result without changing the workspace.",
      fallback:
        "Studio will verify the tool result before showing this change as applied.",
    },
    readback: {
      cancelling: "Studio is cancelling this request before any result is applied.",
      executing: "Studio is applying the change and checking the tool result.",
      executed:
        "Studio received the tool result. Review the current workspace to confirm the outcome.",
      cancelled: "This request was cancelled before a result was applied.",
      failed: "Studio could not apply or verify this request.",
      recoveryNeeded:
        "The request did not finish, and part of the change may still be applied. Review the workspace before continuing.",
      undoFailed:
        "The applied result is still in place because undo did not complete.",
      undone:
        "Studio applied the available undo. Review the current workspace to confirm the result.",
    },
    undo: {
      nothingApplied: "Nothing was applied, so there is nothing to undo.",
      cancelling:
        "Cancellation is in progress. Nothing can be undone unless a result was applied.",
      failed: "The request did not complete, so no undo is available.",
      recoveryAvailable: "Recovery is available for the remaining applied change.",
      recoveryUnavailable:
        "Part of the change may remain. Review the workspace before trying another change.",
      undoFailed:
        "Undo did not complete. The applied change is still in place; try undo again.",
      undone: "This change was undone.",
      executing: "Undo availability will be shown after the request finishes.",
      executedAvailable: "Undo is available for this applied change.",
      executedUnavailable:
        "This change can't be undone here. Describe a new change if you want to replace it.",
      noInverse:
        "This change can't be undone here. Describe a new change if you want to replace it.",
      cannotUndoLocally:
        "This external change can't be undone here. A separate rollback requires confirmation.",
      default: "You can undo this change after it is applied.",
    },
    developerReadback: {
      cancelling: (readback) =>
        `Cancellation is in progress. Verification contract was: ${readback}`,
      executing: (readback) =>
        `Execution is in progress. Verification contract: ${readback}`,
      executed: (readback) =>
        `Tool execution completed. Verification contract: ${readback}`,
      cancelled: (readback) =>
        `Cancelled before execution. Verification contract was: ${readback}`,
      failed: (readback) => `Execution failed. Verification contract was: ${readback}`,
      recoveryNeeded: (readback, rollbackPhrase) =>
        `Execution failed and automatic rollback ${rollbackPhrase}; a partial result may remain. Verification contract was: ${readback}`,
      undoFailed: (readback) =>
        `Undo failed; the applied result remains. Original verification contract: ${readback}`,
      undone: (readback) =>
        `Undo completed. Original verification contract: ${readback}`,
      rollbackDidNotComplete: "did not complete",
      rollbackWasUnavailable: "was unavailable",
    },
    developerUndo: {
      cancelling: (undo) => `Cancellation is in progress. Technical contract: ${undo}`,
      executing: (undo) => `Undo availability is pending. Technical contract: ${undo}`,
      executedAvailable: (undo) => `Undo is available. Technical contract: ${undo}`,
      executedUnavailable: (undo) =>
        `No undo is available for this execution. Technical contract: ${undo}`,
      cancelled: (undo) => `Nothing was applied. Technical contract was: ${undo}`,
      failed: (undo) =>
        `Execution failed before an undo became available. Technical contract was: ${undo}`,
      recoveryAvailable: (undo, rollbackPhrase) =>
        `Recovery undo is available after automatic rollback ${rollbackPhrase}. Technical contract: ${undo}`,
      recoveryUnavailable: (undo, rollbackPhrase) =>
        `No recovery undo is available after automatic rollback ${rollbackPhrase}. Technical contract: ${undo}`,
      undoFailed: (undo) => `Undo failed and may be retried. Technical contract: ${undo}`,
      undone: (undo) => `Undo completed. Technical contract: ${undo}`,
      rollbackFailedPhrase: "failed",
      rollbackWasUnavailablePhrase: "was unavailable",
    },
  },
};

const ko: ChatMessagesCopy = {
  common: { none: "없음" },
  actionFlowTrace: {
    heading: "실행 로직",
    stateTopologyHeading: "상태 토폴로지",
    pathsCount: (count) => `경로 ${count}개`,
    event: "이벤트",
    flow: "플로우",
    domains: "도메인",
    stepOrder: "단계 순서",
    stateDiffHeading: "Preview에 반영된 상태 변화",
    renderer: "렌더러",
  },
  compose: {
    ragPlanLabel: "RAG 플랜",
    buildPlanLabel: "빌드 계획",
    readyLabel: "준비됨",
    summary: (sectionCount, blockedCount) => {
      const tail =
        blockedCount > 0
          ? ` — ${blockedCount}개는 빌드 전에 확인이 필요해요`
          : " — 모두 빌드 준비가 됐어요";
      return `섹션 ${sectionCount}개 계획됨${tail}.`;
    },
    pages: "페이지",
    bindings: "바인딩",
    blockedLabel: "차단됨",
    candidateBlocks: "후보 블록",
    blockedBadge: "차단됨",
    intentRatio: (matched, total) => `의도 일치 ${matched}/${total}`,
    intentScore: (score) => `의도 일치 ${score}`,
    notBound: "미연결",
    evidenceState: (state) => `근거 ${state}`,
    moreCandidates: (count) => `+${count}개 후보 더보기`,
    bindPreviewHeading: "바인딩 미리보기",
    stateLabel: "State",
    actionsLabel: "Actions",
    dataLabel: "Data",
    supported: "지원됨",
    partial: "부분 지원",
    unsupported: "미지원",
  },
  mutation: {
    appliedVerb: {
      add_block: "블록을 추가했어요",
      modify_action: "동작을 수정했어요",
      merge_branch: "변경을 병합했어요",
    },
    appliedPrefix: "적용됨",
    triplesInserted: (count) => `트리플 ${count}개 삽입됨`,
    moreChips: (count) => `+${count}개 더`,
    viewProjectDetails: "프로젝트 세부 정보 보기",
    transportError: "전송 오류",
    shaclViolations: (count) => `SHACL 위반 ${count}건`,
    toolWrapper: (tool) => `(도구: ${tool})`,
    tryFix: "수정 시도",
    anotherUserChangeText: (kind) => {
      const verb =
        kind === "INSERT" ? "추가" : kind === "UPDATE" ? "수정" : "삭제";
      return `다른 사용자가 ${verb} 변경을 했어요`;
    },
    latencyDetail: (latencyMs) => `(지연 ${latencyMs}ms)`,
    anotherUserChangesText: (count) => `다른 사용자가 변경 ${count}건을 했어요`,
    lastLatencyDetail: (lastLatencyMs) => `(최근 ${lastLatencyMs}ms 전)`,
    eventSeqLabel: "순번",
  },
  agentDisclosure: {
    title: "에이전트 작업 완료",
    toolLabel: "도구",
    targetLabel: "대상",
  },
  validation: {
    passedAcross: (passCount, blockCount) =>
      `블록 ${blockCount}개에서 ${passCount}건 통과`,
    violations: (count) => `위반 ${count}건`,
    warningsSuffix: (count) => `, 경고 ${count}건`,
    moreViolations: (count) => `+ 위반 ${count}건 더 · 검사 결과 열기`,
    blockGoneTitle: "이 블록은 더 이상 존재하지 않아요 — 검증을 다시 실행해 최신 결과를 확인하세요.",
  },
  ai: {
    foundInstantly: "즉시 찾았어요 — 검증된 패턴에서 가져왔어요.",
    creatingApp: (pageCount, estimateSeconds) =>
      `앱을 만드는 중이에요 — 페이지 ${pageCount}개 (약 ${estimateSeconds}초)`,
    appReady: "앱이 준비됐어요",
    applied: "적용됨",
  },
  capability: {
    statusZero: {
      pending: "승인하려면 정확한 변경 경로가 필요해요",
      cancelling: "제안 취소 중",
      executing: "제안 적용 중",
      executed: "제안이 적용됐어요",
      cancelled: "제안이 취소됐어요",
      failed: "제안을 적용하지 못했어요",
      "recovery-needed": "제안에 복구가 필요해요",
      "undo-failed": "제안이 적용됐지만 되돌리기에 실패했어요",
      undone: "제안을 되돌렸어요",
      default: "승인하려면 정확한 변경 경로가 필요해요",
    },
    statusWithCount: {
      changeCount: (count) => `변경 ${count}건`,
      cancelling: (changes) => `${changes} 취소 중`,
      applying: (changes) => `${changes} 적용 중`,
      applied: (changes) => `${changes} 적용됨`,
      cancelled: (changes) => `${changes} 취소됨`,
      failed: (changes) => `${changes}을(를) 적용하지 못했어요`,
      recoveryNeeded: (changes) => `${changes}에 복구가 필요해요`,
      undoFailed: (changes) => `${changes} 적용됐지만 되돌리기에 실패했어요`,
      undone: (changes) => `${changes} 되돌림`,
      default: (changes) => `${changes} 검토 대기 중`,
    },
    confirmFailedGeneric: "제안 승인에 실패했어요.",
    cancelUnavailable: "이 승인 요청은 더 이상 사용할 수 없어요.",
    verificationLabel: "검증",
    undoLabel: "되돌리기",
    beforeLabel: "변경 전",
    afterLabel: "변경 후",
    missingChangesWarning:
      "정확한 변경 경로가 없어요. 이 제안을 취소하고 새 제안을 요청하세요.",
    typeToConfirm: (phrase) => `계속하려면 ${phrase} 입력`,
    typeToConfirmHint: "계속하려면 아래 칸에 다음 문구를 그대로 입력하세요:",
    changeTargets: {
      pageMeta: {
        title: "페이지 제목",
        description: "페이지 설명",
        route: "페이지 주소",
        layout: "페이지 배치",
      },
    },
    statusSpans: {
      applying: "적용 중...",
      cancelling: "취소 중...",
      applied: "적용됨",
      cancelled: "취소됨",
      needsRecovery: "복구 필요",
      undone: "되돌림",
    },
    actions: {
      confirm: "확인",
      cancel: "취소",
      requestChanges: "수정 요청",
      undo: "되돌리기",
      recover: "복구",
      retryUndo: "되돌리기 재시도",
      showAllChanges: (count) => `변경 ${count}건 모두 보기`,
      showFewerChanges: "변경 내용 간략히 보기",
    },
    pendingReadback: {
      secret: "Studio가 비밀 값을 노출하지 않고 연결을 확인할 거예요.",
      release: "Studio가 릴리스 결과를 검토용으로 보존해요. 실제 반영 여부는 대상 위치를 확인한 뒤 판단하세요.",
      exportBundle: "Studio가 내보낸 파일을 준비 완료로 표시하기 전에 확인할 거예요.",
      importIngest:
        "Studio가 이 워크스페이스에 가져온 결과를 확인할 거예요. 명시적으로 동기화하기 전까지 외부 원본은 그대로 유지돼요.",
      openNavigate: "Studio가 요청한 워크스페이스가 오류 없이 열리는지 확인할 거예요.",
      validateReview: "Studio가 워크스페이스를 바꾸지 않고 검사 결과만 보여줄 거예요.",
      fallback: "Studio가 이 변경을 적용됨으로 표시하기 전에 도구 결과를 확인할 거예요.",
    },
    readback: {
      cancelling: "Studio가 결과가 적용되기 전에 이 요청을 취소하고 있어요.",
      executing: "Studio가 변경을 적용하고 도구 결과를 확인하고 있어요.",
      executed: "Studio가 도구 결과를 받았어요. 현재 워크스페이스를 확인해 결과를 검토하세요.",
      cancelled: "결과가 적용되기 전에 이 요청이 취소됐어요.",
      failed: "Studio가 이 요청을 적용하거나 확인하지 못했어요.",
      recoveryNeeded:
        "요청이 끝까지 처리되지 않았고, 일부 변경이 여전히 적용돼 있을 수 있어요. 계속하기 전에 워크스페이스를 확인하세요.",
      undoFailed: "되돌리기가 완료되지 않아 적용된 결과가 그대로 남아 있어요.",
      undone: "Studio가 가능한 되돌리기를 적용했어요. 현재 워크스페이스를 확인해 결과를 검토하세요.",
    },
    undo: {
      nothingApplied: "적용된 내용이 없어서 되돌릴 것이 없어요.",
      cancelling: "취소가 진행 중이에요. 결과가 적용된 경우가 아니면 되돌릴 것이 없어요.",
      failed: "요청이 완료되지 않아 되돌리기를 사용할 수 없어요.",
      recoveryAvailable: "남아 있는 적용된 변경에 대해 복구를 사용할 수 있어요.",
      recoveryUnavailable: "일부 변경이 남아 있을 수 있어요. 다른 변경을 시도하기 전에 워크스페이스를 확인하세요.",
      undoFailed: "되돌리기가 완료되지 않았어요. 적용된 변경은 그대로 남아 있으니 다시 시도하세요.",
      undone: "이 변경을 되돌렸어요.",
      executing: "요청이 끝나면 되돌리기 가능 여부가 표시돼요.",
      executedAvailable: "이 적용된 변경에 대해 되돌리기를 사용할 수 있어요.",
      executedUnavailable: "이 변경은 여기서 되돌릴 수 없어요. 대체하려면 새 변경을 설명해 주세요.",
      noInverse: "이 변경은 여기서 되돌릴 수 없어요. 대체하려면 새 변경을 설명해 주세요.",
      cannotUndoLocally: "이 외부 변경은 여기서 되돌릴 수 없어요. 별도 롤백에는 확인이 필요해요.",
      default: "이 변경은 적용된 뒤에 되돌릴 수 있어요.",
    },
    developerReadback: {
      cancelling: (readback) => `취소가 진행 중이에요. 검증 계약(이전): ${readback}`,
      executing: (readback) => `실행이 진행 중이에요. 검증 계약: ${readback}`,
      executed: (readback) => `도구 실행이 완료됐어요. 검증 계약: ${readback}`,
      cancelled: (readback) => `실행 전에 취소됐어요. 검증 계약(이전): ${readback}`,
      failed: (readback) => `실행이 실패했어요. 검증 계약(이전): ${readback}`,
      recoveryNeeded: (readback, rollbackPhrase) =>
        `실행이 실패했고 자동 롤백이 ${rollbackPhrase}; 일부 결과가 남아 있을 수 있어요. 검증 계약(이전): ${readback}`,
      undoFailed: (readback) => `되돌리기에 실패했어요. 적용된 결과는 그대로 남아 있어요. 원본 검증 계약: ${readback}`,
      undone: (readback) => `되돌리기가 완료됐어요. 원본 검증 계약: ${readback}`,
      rollbackDidNotComplete: "완료되지 않았어요",
      rollbackWasUnavailable: "사용할 수 없었어요",
    },
    developerUndo: {
      cancelling: (undo) => `취소가 진행 중이에요. 기술 계약: ${undo}`,
      executing: (undo) => `되돌리기 가능 여부를 확인 중이에요. 기술 계약: ${undo}`,
      executedAvailable: (undo) => `되돌리기를 사용할 수 있어요. 기술 계약: ${undo}`,
      executedUnavailable: (undo) => `이 실행에는 되돌리기를 사용할 수 없어요. 기술 계약: ${undo}`,
      cancelled: (undo) => `적용된 내용이 없어요. 기술 계약(이전): ${undo}`,
      failed: (undo) => `되돌리기를 사용할 수 있게 되기 전에 실행이 실패했어요. 기술 계약(이전): ${undo}`,
      recoveryAvailable: (undo, rollbackPhrase) =>
        `자동 롤백이 ${rollbackPhrase} 뒤, 복구 되돌리기를 사용할 수 있어요. 기술 계약: ${undo}`,
      recoveryUnavailable: (undo, rollbackPhrase) =>
        `자동 롤백이 ${rollbackPhrase} 뒤에도 복구 되돌리기를 사용할 수 없어요. 기술 계약: ${undo}`,
      undoFailed: (undo) => `되돌리기에 실패했어요. 다시 시도할 수 있어요. 기술 계약: ${undo}`,
      undone: (undo) => `되돌리기가 완료됐어요. 기술 계약: ${undo}`,
      rollbackFailedPhrase: "실패한",
      rollbackWasUnavailablePhrase: "사용할 수 없었던",
    },
  },
};

const ja: ChatMessagesCopy = {
  common: { none: "なし" },
  actionFlowTrace: {
    heading: "実行ロジック",
    stateTopologyHeading: "状態トポロジー",
    pathsCount: (count) => `パス${count}件`,
    event: "イベント",
    flow: "フロー",
    domains: "ドメイン",
    stepOrder: "ステップ順序",
    stateDiffHeading: "Previewに反映された状態差分",
    renderer: "レンダラー",
  },
  compose: {
    ragPlanLabel: "RAGプラン",
    buildPlanLabel: "ビルドプラン",
    readyLabel: "準備完了",
    summary: (sectionCount, blockedCount) => {
      const tail =
        blockedCount > 0
          ? ` — ${blockedCount}件はビルド前に確認が必要です`
          : " — すべてビルド準備が完了しています";
      return `セクション${sectionCount}件を計画${tail}。`;
    },
    pages: "ページ",
    bindings: "バインディング",
    blockedLabel: "ブロック中",
    candidateBlocks: "候補ブロック",
    blockedBadge: "ブロック中",
    intentRatio: (matched, total) => `意図一致 ${matched}/${total}`,
    intentScore: (score) => `意図一致 ${score}`,
    notBound: "未バインド",
    evidenceState: (state) => `根拠 ${state}`,
    moreCandidates: (count) => `他${count}件の候補`,
    bindPreviewHeading: "バインドプレビュー",
    stateLabel: "State",
    actionsLabel: "Actions",
    dataLabel: "Data",
    supported: "対応",
    partial: "一部対応",
    unsupported: "非対応",
  },
  mutation: {
    appliedVerb: {
      add_block: "ブロックを追加しました",
      modify_action: "動作を更新しました",
      merge_branch: "変更をマージしました",
    },
    appliedPrefix: "適用済み",
    triplesInserted: (count) => `トリプル${count}件を挿入`,
    moreChips: (count) => `他${count}件`,
    viewProjectDetails: "プロジェクトの詳細を見る",
    transportError: "通信エラー",
    shaclViolations: (count) => `SHACL違反${count}件`,
    toolWrapper: (tool) => `(ツール: ${tool})`,
    tryFix: "修正を試す",
    anotherUserChangeText: (kind) => {
      const verb =
        kind === "INSERT" ? "追加" : kind === "UPDATE" ? "更新" : "削除";
      return `別のユーザーが${verb}の変更を行いました`;
    },
    latencyDetail: (latencyMs) => `(遅延 ${latencyMs}ms)`,
    anotherUserChangesText: (count) => `別のユーザーが${count}件の変更を行いました`,
    lastLatencyDetail: (lastLatencyMs) => `(直近 ${lastLatencyMs}ms前)`,
    eventSeqLabel: "順序",
  },
  agentDisclosure: {
    title: "エージェント操作が完了しました",
    toolLabel: "ツール",
    targetLabel: "対象",
  },
  validation: {
    passedAcross: (passCount, blockCount) =>
      `ブロック${blockCount}件中${passCount}件が合格`,
    violations: (count) => `違反${count}件`,
    warningsSuffix: (count) => `、警告${count}件`,
    moreViolations: (count) => `他${count}件の違反 · 検査結果を開く`,
    blockGoneTitle: "このブロックはすでに存在しません — 検証を再実行して最新の結果を確認してください。",
  },
  ai: {
    foundInstantly: "即座に見つかりました — 検証済みのパターンから取得しました。",
    creatingApp: (pageCount, estimateSeconds) =>
      `アプリを作成中です — ${pageCount}ページ (約${estimateSeconds}秒)`,
    appReady: "アプリの準備ができました",
    applied: "適用済み",
  },
  capability: {
    statusZero: {
      pending: "承認には正確な変更パスが必要です",
      cancelling: "提案をキャンセル中",
      executing: "提案を適用中",
      executed: "提案を適用しました",
      cancelled: "提案をキャンセルしました",
      failed: "提案を適用できませんでした",
      "recovery-needed": "提案に復旧が必要です",
      "undo-failed": "提案は適用されましたが、取り消しに失敗しました",
      undone: "提案を取り消しました",
      default: "承認には正確な変更パスが必要です",
    },
    statusWithCount: {
      changeCount: (count) => `変更${count}件`,
      cancelling: (changes) => `${changes}をキャンセル中`,
      applying: (changes) => `${changes}を適用中`,
      applied: (changes) => `${changes}を適用しました`,
      cancelled: (changes) => `${changes}をキャンセルしました`,
      failed: (changes) => `${changes}を適用できませんでした`,
      recoveryNeeded: (changes) => `${changes}に復旧が必要です`,
      undoFailed: (changes) => `${changes}は適用されましたが、取り消しに失敗しました`,
      undone: (changes) => `${changes}を取り消しました`,
      default: (changes) => `${changes}が確認待ちです`,
    },
    confirmFailedGeneric: "提案の承認に失敗しました。",
    cancelUnavailable: "この承認はすでに利用できません。",
    verificationLabel: "検証",
    undoLabel: "取り消し",
    beforeLabel: "変更前",
    afterLabel: "変更後",
    missingChangesWarning:
      "正確な変更パスがありません。この提案をキャンセルし、新しい提案を依頼してください。",
    typeToConfirm: (phrase) => `続行するには${phrase}と入力`,
    typeToConfirmHint: "続けるには、下の欄に次の語句をそのまま入力してください:",
    changeTargets: {
      pageMeta: {
        title: "ページのタイトル",
        description: "ページの説明",
        route: "ページのアドレス",
        layout: "ページの配置",
      },
    },
    statusSpans: {
      applying: "適用中...",
      cancelling: "キャンセル中...",
      applied: "適用済み",
      cancelled: "キャンセル済み",
      needsRecovery: "復旧が必要",
      undone: "取り消し済み",
    },
    actions: {
      confirm: "確認",
      cancel: "キャンセル",
      requestChanges: "修正を依頼",
      undo: "取り消し",
      recover: "復旧",
      retryUndo: "取り消しを再試行",
      showAllChanges: (count) => `変更を全${count}件表示`,
      showFewerChanges: "変更の表示を折りたたむ",
    },
    pendingReadback: {
      secret: "Studioは機密情報を公開せずに接続を確認します。",
      release: "Studioはリリース結果を確認用に保持します。ライブ扱いにする前に配信先を確認してください。",
      exportBundle: "Studioは準備完了とマークする前にエクスポートされたファイルを確認します。",
      importIngest:
        "Studioはこのワークスペースに取り込んだ結果を確認します。明示的に同期するまで外部ソースは変更されません。",
      openNavigate: "Studioは要求されたワークスペースがエラーなく開くことを確認します。",
      validateReview: "Studioはワークスペースを変更せずに検査結果だけを表示します。",
      fallback: "Studioはこの変更を適用済みと表示する前にツールの結果を確認します。",
    },
    readback: {
      cancelling: "Studioは結果が適用される前にこのリクエストをキャンセルしています。",
      executing: "Studioは変更を適用し、ツールの結果を確認しています。",
      executed: "Studioはツールの結果を受け取りました。現在のワークスペースで結果を確認してください。",
      cancelled: "結果が適用される前にこのリクエストはキャンセルされました。",
      failed: "Studioはこのリクエストを適用または確認できませんでした。",
      recoveryNeeded:
        "リクエストが完了せず、一部の変更が適用されたままの可能性があります。続行する前にワークスペースを確認してください。",
      undoFailed: "取り消しが完了しなかったため、適用済みの結果がそのまま残っています。",
      undone: "Studioは可能な取り消しを適用しました。現在のワークスペースで結果を確認してください。",
    },
    undo: {
      nothingApplied: "何も適用されていないため、取り消す内容がありません。",
      cancelling: "キャンセル処理中です。結果が適用されていない限り取り消す内容はありません。",
      failed: "リクエストが完了しなかったため、取り消しは利用できません。",
      recoveryAvailable: "残っている適用済みの変更について復旧を利用できます。",
      recoveryUnavailable: "一部の変更が残っている可能性があります。別の変更を試す前にワークスペースを確認してください。",
      undoFailed: "取り消しが完了しませんでした。適用済みの変更はそのまま残っているため、もう一度試してください。",
      undone: "この変更を取り消しました。",
      executing: "リクエストが完了すると取り消しの可否が表示されます。",
      executedAvailable: "この適用済みの変更は取り消しできます。",
      executedUnavailable: "この変更はここでは取り消せません。置き換えたい場合は新しい変更を説明してください。",
      noInverse: "この変更はここでは取り消せません。置き換えたい場合は新しい変更を説明してください。",
      cannotUndoLocally: "この外部変更はここでは取り消せません。別途ロールバックには確認が必要です。",
      default: "この変更は適用後に取り消せます。",
    },
    developerReadback: {
      cancelling: (readback) => `キャンセル処理中です。検証契約(変更前): ${readback}`,
      executing: (readback) => `実行中です。検証契約: ${readback}`,
      executed: (readback) => `ツールの実行が完了しました。検証契約: ${readback}`,
      cancelled: (readback) => `実行前にキャンセルされました。検証契約(変更前): ${readback}`,
      failed: (readback) => `実行に失敗しました。検証契約(変更前): ${readback}`,
      recoveryNeeded: (readback, rollbackPhrase) =>
        `実行に失敗し、自動ロールバックは${rollbackPhrase}。一部の結果が残っている可能性があります。検証契約(変更前): ${readback}`,
      undoFailed: (readback) => `取り消しに失敗しました。適用済みの結果が残っています。元の検証契約: ${readback}`,
      undone: (readback) => `取り消しが完了しました。元の検証契約: ${readback}`,
      rollbackDidNotComplete: "完了しませんでした",
      rollbackWasUnavailable: "利用できませんでした",
    },
    developerUndo: {
      cancelling: (undo) => `キャンセル処理中です。技術契約: ${undo}`,
      executing: (undo) => `取り消しの可否を確認中です。技術契約: ${undo}`,
      executedAvailable: (undo) => `取り消しを利用できます。技術契約: ${undo}`,
      executedUnavailable: (undo) => `この実行には取り消しを利用できません。技術契約: ${undo}`,
      cancelled: (undo) => `何も適用されていません。技術契約(変更前): ${undo}`,
      failed: (undo) => `取り消しが利用可能になる前に実行が失敗しました。技術契約(変更前): ${undo}`,
      recoveryAvailable: (undo, rollbackPhrase) =>
        `自動ロールバックが${rollbackPhrase}後、復旧の取り消しを利用できます。技術契約: ${undo}`,
      recoveryUnavailable: (undo, rollbackPhrase) =>
        `自動ロールバックが${rollbackPhrase}後も、復旧の取り消しは利用できません。技術契約: ${undo}`,
      undoFailed: (undo) => `取り消しに失敗しました。再試行できます。技術契約: ${undo}`,
      undone: (undo) => `取り消しが完了しました。技術契約: ${undo}`,
      rollbackFailedPhrase: "失敗した",
      rollbackWasUnavailablePhrase: "利用できなかった",
    },
  },
};

const zh: ChatMessagesCopy = {
  common: { none: "无" },
  actionFlowTrace: {
    heading: "运行逻辑",
    stateTopologyHeading: "状态拓扑",
    pathsCount: (count) => `${count}条路径`,
    event: "事件",
    flow: "流程",
    domains: "域",
    stepOrder: "步骤顺序",
    stateDiffHeading: "已反映到Preview的状态差异",
    renderer: "渲染器",
  },
  compose: {
    ragPlanLabel: "RAG计划",
    buildPlanLabel: "构建计划",
    readyLabel: "就绪",
    summary: (sectionCount, blockedCount) => {
      const tail =
        blockedCount > 0
          ? ` — 有${blockedCount}项需要在构建前查看`
          : " — 全部已就绪，可以构建";
      return `已计划${sectionCount}个部分${tail}。`;
    },
    pages: "页面",
    bindings: "绑定",
    blockedLabel: "已阻止",
    candidateBlocks: "候选区块",
    blockedBadge: "已阻止",
    intentRatio: (matched, total) => `意图匹配 ${matched}/${total}`,
    intentScore: (score) => `意图匹配 ${score}`,
    notBound: "未绑定",
    evidenceState: (state) => `证据 ${state}`,
    moreCandidates: (count) => `还有${count}个候选`,
    bindPreviewHeading: "绑定预览",
    stateLabel: "State",
    actionsLabel: "Actions",
    dataLabel: "Data",
    supported: "支持",
    partial: "部分支持",
    unsupported: "不支持",
  },
  mutation: {
    appliedVerb: {
      add_block: "已添加区块",
      modify_action: "已更新行为",
      merge_branch: "已合并变更",
    },
    appliedPrefix: "已应用",
    triplesInserted: (count) => `已插入${count}条三元组`,
    moreChips: (count) => `还有${count}个`,
    viewProjectDetails: "查看项目详情",
    transportError: "传输错误",
    shaclViolations: (count) => `${count}项SHACL违规`,
    toolWrapper: (tool) => `(工具: ${tool})`,
    tryFix: "尝试修复",
    anotherUserChangeText: (kind) => {
      const verb = kind === "INSERT" ? "新增" : kind === "UPDATE" ? "更新" : "删除";
      return `另一位用户进行了${verb}变更`;
    },
    latencyDetail: (latencyMs) => `(延迟 ${latencyMs}ms)`,
    anotherUserChangesText: (count) => `另一位用户进行了${count}项变更`,
    lastLatencyDetail: (lastLatencyMs) => `(最近 ${lastLatencyMs}ms前)`,
    eventSeqLabel: "序号",
  },
  agentDisclosure: {
    title: "代理操作已完成",
    toolLabel: "工具",
    targetLabel: "目标",
  },
  validation: {
    passedAcross: (passCount, blockCount) =>
      `在${blockCount}个区块中通过${passCount}项`,
    violations: (count) => `${count}项违规`,
    warningsSuffix: (count) => `，${count}项警告`,
    moreViolations: (count) => `+ 还有${count}项违规 · 查看检查结果`,
    blockGoneTitle: "此区块已不存在 — 请重新运行验证以获取最新结果。",
  },
  ai: {
    foundInstantly: "已立即找到 — 来自已验证的模式。",
    creatingApp: (pageCount, estimateSeconds) =>
      `正在创建您的应用 — ${pageCount}个页面 (约${estimateSeconds}秒)`,
    appReady: "应用已就绪",
    applied: "已应用",
  },
  capability: {
    statusZero: {
      pending: "批准前需要准确的变更路径",
      cancelling: "正在取消提案",
      executing: "正在应用提案",
      executed: "提案已应用",
      cancelled: "提案已取消",
      failed: "提案未能应用",
      "recovery-needed": "提案需要恢复",
      "undo-failed": "提案已应用；撤销失败",
      undone: "提案已撤销",
      default: "批准前需要准确的变更路径",
    },
    statusWithCount: {
      changeCount: (count) => `${count}项变更`,
      cancelling: (changes) => `正在取消${changes}`,
      applying: (changes) => `正在应用${changes}`,
      applied: (changes) => `${changes}已应用`,
      cancelled: (changes) => `${changes}已取消`,
      failed: (changes) => `${changes}未能应用`,
      recoveryNeeded: (changes) => `${changes}需要恢复`,
      undoFailed: (changes) => `${changes}已应用；撤销失败`,
      undone: (changes) => `${changes}已撤销`,
      default: (changes) => `${changes}等待审核`,
    },
    confirmFailedGeneric: "提案审批失败。",
    cancelUnavailable: "此审批已不可用。",
    verificationLabel: "验证",
    undoLabel: "撤销",
    beforeLabel: "变更前",
    afterLabel: "变更后",
    missingChangesWarning: "缺少准确的变更路径。请取消此提案并请求新的提案。",
    typeToConfirm: (phrase) => `输入${phrase}以继续`,
    typeToConfirmHint: "要继续，请在下方输入框中原样输入以下短语：",
    changeTargets: {
      pageMeta: {
        title: "页面标题",
        description: "页面说明",
        route: "页面地址",
        layout: "页面布局",
      },
    },
    statusSpans: {
      applying: "正在应用…",
      cancelling: "正在取消…",
      applied: "已应用",
      cancelled: "已取消",
      needsRecovery: "需要恢复",
      undone: "已撤销",
    },
    actions: {
      confirm: "确认",
      cancel: "取消",
      requestChanges: "请求修改",
      undo: "撤销",
      recover: "恢复",
      retryUndo: "重试撤销",
      showAllChanges: (count) => `显示全部${count}项变更`,
      showFewerChanges: "收起变更",
    },
    pendingReadback: {
      secret: "Studio会在不暴露密钥的情况下验证连接。",
      release: "Studio会保留发布结果供审核。请在视为已上线前确认目标位置。",
      exportBundle: "Studio会在标记为就绪前验证导出的文件。",
      importIngest: "Studio会验证此工作区中导入的结果。除非您明确同步，否则外部来源保持不变。",
      openNavigate: "Studio会确认所请求的工作区能够无加载错误地打开。",
      validateReview: "Studio会在不更改工作区的情况下显示检查结果。",
      fallback: "Studio会在将此变更显示为已应用前验证工具结果。",
    },
    readback: {
      cancelling: "Studio正在应用结果前取消此请求。",
      executing: "Studio正在应用变更并核对工具结果。",
      executed: "Studio已收到工具结果。请查看当前工作区以确认结果。",
      cancelled: "此请求在结果应用前已被取消。",
      failed: "Studio未能应用或验证此请求。",
      recoveryNeeded: "请求未完成，部分变更可能仍已应用。请在继续前查看工作区。",
      undoFailed: "由于撤销未完成，已应用的结果仍然保留。",
      undone: "Studio已应用可用的撤销。请查看当前工作区以确认结果。",
    },
    undo: {
      nothingApplied: "没有已应用的内容，因此无需撤销。",
      cancelling: "取消正在进行中。除非结果已应用，否则无法撤销。",
      failed: "请求未完成，因此没有可用的撤销。",
      recoveryAvailable: "可以为剩余已应用的变更使用恢复。",
      recoveryUnavailable: "部分变更可能仍然保留。请在尝试其他变更前查看工作区。",
      undoFailed: "撤销未完成。已应用的变更仍然保留，请再试一次。",
      undone: "此变更已撤销。",
      executing: "请求完成后将显示是否可撤销。",
      executedAvailable: "此已应用的变更可以撤销。",
      executedUnavailable: "此变更无法在此撤销。如需替换，请说明新的变更。",
      noInverse: "此变更无法在此撤销。如需替换，请说明新的变更。",
      cannotUndoLocally: "此外部变更无法在此撤销。单独的回滚需要确认。",
      default: "此变更在应用后可以撤销。",
    },
    developerReadback: {
      cancelling: (readback) => `正在取消中。验证契约(变更前): ${readback}`,
      executing: (readback) => `正在执行中。验证契约: ${readback}`,
      executed: (readback) => `工具执行已完成。验证契约: ${readback}`,
      cancelled: (readback) => `执行前已取消。验证契约(变更前): ${readback}`,
      failed: (readback) => `执行失败。验证契约(变更前): ${readback}`,
      recoveryNeeded: (readback, rollbackPhrase) =>
        `执行失败，自动回滚${rollbackPhrase}；可能仍有部分结果保留。验证契约(变更前): ${readback}`,
      undoFailed: (readback) => `撤销失败；已应用的结果仍然保留。原始验证契约: ${readback}`,
      undone: (readback) => `撤销已完成。原始验证契约: ${readback}`,
      rollbackDidNotComplete: "未完成",
      rollbackWasUnavailable: "不可用",
    },
    developerUndo: {
      cancelling: (undo) => `正在取消中。技术契约: ${undo}`,
      executing: (undo) => `撤销可用性待确定。技术契约: ${undo}`,
      executedAvailable: (undo) => `可以撤销。技术契约: ${undo}`,
      executedUnavailable: (undo) => `此次执行没有可用的撤销。技术契约: ${undo}`,
      cancelled: (undo) => `未应用任何内容。技术契约(变更前): ${undo}`,
      failed: (undo) => `在撤销可用之前执行已失败。技术契约(变更前): ${undo}`,
      recoveryAvailable: (undo, rollbackPhrase) =>
        `自动回滚${rollbackPhrase}后，可以使用恢复撤销。技术契约: ${undo}`,
      recoveryUnavailable: (undo, rollbackPhrase) =>
        `自动回滚${rollbackPhrase}后，仍没有可用的恢复撤销。技术契约: ${undo}`,
      undoFailed: (undo) => `撤销失败，可以重试。技术契约: ${undo}`,
      undone: (undo) => `撤销已完成。技术契约: ${undo}`,
      rollbackFailedPhrase: "已失败",
      rollbackWasUnavailablePhrase: "不可用",
    },
  },
};

const zhTW: ChatMessagesCopy = {
  common: { none: "無" },
  actionFlowTrace: {
    heading: "執行邏輯",
    stateTopologyHeading: "狀態拓撲",
    pathsCount: (count) => `${count}條路徑`,
    event: "事件",
    flow: "流程",
    domains: "網域",
    stepOrder: "步驟順序",
    stateDiffHeading: "已反映到Preview的狀態差異",
    renderer: "渲染器",
  },
  compose: {
    ragPlanLabel: "RAG計畫",
    buildPlanLabel: "建置計畫",
    readyLabel: "就緒",
    summary: (sectionCount, blockedCount) => {
      const tail =
        blockedCount > 0
          ? ` — 有${blockedCount}項需要在建置前確認`
          : " — 全部已就緒，可以建置";
      return `已規劃${sectionCount}個區段${tail}。`;
    },
    pages: "頁面",
    bindings: "綁定",
    blockedLabel: "已封鎖",
    candidateBlocks: "候選區塊",
    blockedBadge: "已封鎖",
    intentRatio: (matched, total) => `意圖符合 ${matched}/${total}`,
    intentScore: (score) => `意圖符合 ${score}`,
    notBound: "未綁定",
    evidenceState: (state) => `證據 ${state}`,
    moreCandidates: (count) => `還有${count}個候選`,
    bindPreviewHeading: "綁定預覽",
    stateLabel: "State",
    actionsLabel: "Actions",
    dataLabel: "Data",
    supported: "支援",
    partial: "部分支援",
    unsupported: "不支援",
  },
  mutation: {
    appliedVerb: {
      add_block: "已新增區塊",
      modify_action: "已更新行為",
      merge_branch: "已合併變更",
    },
    appliedPrefix: "已套用",
    triplesInserted: (count) => `已插入${count}筆三元組`,
    moreChips: (count) => `還有${count}個`,
    viewProjectDetails: "查看專案詳細資料",
    transportError: "傳輸錯誤",
    shaclViolations: (count) => `${count}項SHACL違規`,
    toolWrapper: (tool) => `(工具: ${tool})`,
    tryFix: "嘗試修正",
    anotherUserChangeText: (kind) => {
      const verb = kind === "INSERT" ? "新增" : kind === "UPDATE" ? "更新" : "刪除";
      return `另一位使用者進行了${verb}變更`;
    },
    latencyDetail: (latencyMs) => `(延遲 ${latencyMs}ms)`,
    anotherUserChangesText: (count) => `另一位使用者進行了${count}項變更`,
    lastLatencyDetail: (lastLatencyMs) => `(最近 ${lastLatencyMs}ms前)`,
    eventSeqLabel: "序號",
  },
  agentDisclosure: {
    title: "代理操作已完成",
    toolLabel: "工具",
    targetLabel: "目標",
  },
  validation: {
    passedAcross: (passCount, blockCount) => `在${blockCount}個區塊中通過${passCount}項`,
    violations: (count) => `${count}項違規`,
    warningsSuffix: (count) => `，${count}項警告`,
    moreViolations: (count) => `+ 還有${count}項違規 · 開啟檢查結果`,
    blockGoneTitle: "此區塊已不存在 — 請重新執行驗證以取得最新結果。",
  },
  ai: {
    foundInstantly: "已立即找到 — 來自已驗證的模式。",
    creatingApp: (pageCount, estimateSeconds) =>
      `正在建立您的應用程式 — ${pageCount}個頁面 (約${estimateSeconds}秒)`,
    appReady: "應用程式已就緒",
    applied: "已套用",
  },
  capability: {
    statusZero: {
      pending: "核准前需要準確的變更路徑",
      cancelling: "正在取消提案",
      executing: "正在套用提案",
      executed: "提案已套用",
      cancelled: "提案已取消",
      failed: "提案未能套用",
      "recovery-needed": "提案需要復原",
      "undo-failed": "提案已套用；復原失敗",
      undone: "提案已復原",
      default: "核准前需要準確的變更路徑",
    },
    statusWithCount: {
      changeCount: (count) => `${count}項變更`,
      cancelling: (changes) => `正在取消${changes}`,
      applying: (changes) => `正在套用${changes}`,
      applied: (changes) => `${changes}已套用`,
      cancelled: (changes) => `${changes}已取消`,
      failed: (changes) => `${changes}未能套用`,
      recoveryNeeded: (changes) => `${changes}需要復原`,
      undoFailed: (changes) => `${changes}已套用；復原失敗`,
      undone: (changes) => `${changes}已復原`,
      default: (changes) => `${changes}等待審閱`,
    },
    confirmFailedGeneric: "提案核准失敗。",
    cancelUnavailable: "此核准已不可用。",
    verificationLabel: "驗證",
    undoLabel: "復原",
    beforeLabel: "變更前",
    afterLabel: "變更後",
    missingChangesWarning: "缺少準確的變更路徑。請取消此提案並要求新的提案。",
    typeToConfirm: (phrase) => `輸入${phrase}以繼續`,
    typeToConfirmHint: "要繼續，請在下方輸入框中原樣輸入以下短語：",
    changeTargets: {
      pageMeta: {
        title: "頁面標題",
        description: "頁面說明",
        route: "頁面位址",
        layout: "頁面版面",
      },
    },
    statusSpans: {
      applying: "套用中…",
      cancelling: "取消中…",
      applied: "已套用",
      cancelled: "已取消",
      needsRecovery: "需要復原",
      undone: "已復原",
    },
    actions: {
      confirm: "確認",
      cancel: "取消",
      requestChanges: "要求修改",
      undo: "復原",
      recover: "回復",
      retryUndo: "重試復原",
      showAllChanges: (count) => `顯示全部${count}項變更`,
      showFewerChanges: "收合變更",
    },
    pendingReadback: {
      secret: "Studio 會在不揭露密鑰的情況下驗證連線。",
      release: "Studio 會保留發布結果供審閱。請在視為上線前確認目的地。",
      exportBundle: "Studio 會在標記為就緒前驗證匯出的檔案。",
      importIngest: "Studio 會驗證此工作區中匯入的結果。除非您明確同步，否則外部來源保持不變。",
      openNavigate: "Studio 會確認所要求的工作區能在沒有載入錯誤的情況下開啟。",
      validateReview: "Studio 會在不變更工作區的情況下顯示檢查結果。",
      fallback: "Studio 會在將此變更顯示為已套用前驗證工具結果。",
    },
    readback: {
      cancelling: "Studio 正在於結果套用前取消此請求。",
      executing: "Studio 正在套用變更並核對工具結果。",
      executed: "Studio 已收到工具結果。請查看目前的工作區以確認結果。",
      cancelled: "此請求在結果套用前已被取消。",
      failed: "Studio 未能套用或驗證此請求。",
      recoveryNeeded: "請求未完成，部分變更可能仍已套用。請在繼續前查看工作區。",
      undoFailed: "由於復原未完成，已套用的結果仍然保留。",
      undone: "Studio 已套用可用的復原。請查看目前的工作區以確認結果。",
    },
    undo: {
      nothingApplied: "沒有已套用的內容，因此無需復原。",
      cancelling: "取消正在進行中。除非結果已套用，否則無法復原。",
      failed: "請求未完成，因此沒有可用的復原。",
      recoveryAvailable: "可以為剩餘已套用的變更使用復原。",
      recoveryUnavailable: "部分變更可能仍然保留。請在嘗試其他變更前查看工作區。",
      undoFailed: "復原未完成。已套用的變更仍然保留，請再試一次。",
      undone: "此變更已復原。",
      executing: "請求完成後會顯示是否可復原。",
      executedAvailable: "此已套用的變更可以復原。",
      executedUnavailable: "此變更無法在這裡復原。如需取代，請說明新的變更。",
      noInverse: "此變更無法在這裡復原。如需取代，請說明新的變更。",
      cannotUndoLocally: "此外部變更無法在這裡復原。個別的回復需要確認。",
      default: "此變更在套用後可以復原。",
    },
    developerReadback: {
      cancelling: (readback) => `正在取消中。驗證合約(變更前): ${readback}`,
      executing: (readback) => `正在執行中。驗證合約: ${readback}`,
      executed: (readback) => `工具執行已完成。驗證合約: ${readback}`,
      cancelled: (readback) => `執行前已取消。驗證合約(變更前): ${readback}`,
      failed: (readback) => `執行失敗。驗證合約(變更前): ${readback}`,
      recoveryNeeded: (readback, rollbackPhrase) =>
        `執行失敗，自動回復${rollbackPhrase}；可能仍有部分結果保留。驗證合約(變更前): ${readback}`,
      undoFailed: (readback) => `復原失敗；已套用的結果仍然保留。原始驗證合約: ${readback}`,
      undone: (readback) => `復原已完成。原始驗證合約: ${readback}`,
      rollbackDidNotComplete: "未完成",
      rollbackWasUnavailable: "不可用",
    },
    developerUndo: {
      cancelling: (undo) => `正在取消中。技術合約: ${undo}`,
      executing: (undo) => `復原可用性待確認。技術合約: ${undo}`,
      executedAvailable: (undo) => `可以復原。技術合約: ${undo}`,
      executedUnavailable: (undo) => `此次執行沒有可用的復原。技術合約: ${undo}`,
      cancelled: (undo) => `未套用任何內容。技術合約(變更前): ${undo}`,
      failed: (undo) => `在復原可用之前執行已失敗。技術合約(變更前): ${undo}`,
      recoveryAvailable: (undo, rollbackPhrase) =>
        `自動回復${rollbackPhrase}後，可以使用復原的還原。技術合約: ${undo}`,
      recoveryUnavailable: (undo, rollbackPhrase) =>
        `自動回復${rollbackPhrase}後，仍沒有可用的復原還原。技術合約: ${undo}`,
      undoFailed: (undo) => `復原失敗，可以重試。技術合約: ${undo}`,
      undone: (undo) => `復原已完成。技術合約: ${undo}`,
      rollbackFailedPhrase: "已失敗",
      rollbackWasUnavailablePhrase: "不可用",
    },
  },
};

const COPY: Record<LocaleCode, ChatMessagesCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getChatMessagesCopy(locale: LocaleCode): ChatMessagesCopy {
  return COPY[locale];
}
